/**
 * The places as rows (#6559 rounds 17-18): the picker's root with drives on
 * and the phone's Documents root are Drive's shape — My Drive, Shared drives
 * and Connected apps, three rows that drill in — and the Shared drives and
 * Connected apps listings are that group's places as rows, one per drive or
 * connection. A row is a way in, not a thing to select: never tickable.
 */
import React from "react";
import {
  CaretRightIcon,
  HardDrivesIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import type { Places } from "./api";
import { MY_DRIVE_LABEL, connectionDisplayName } from "./places";
import AppMark from "../AppMark";

export interface PlaceRowTarget {
  driveId: string | null;
  path: string[];
  /** The Shared drives listing, rather than a place. */
  view?: "drives";
}

export interface PlaceRowItem {
  key: string;
  label: string;
  /** A second line under the label: what kind of drive, how many rows the
   *  listing behind the row holds. */
  lede: string | null;
  icon: React.ReactNode;
  target: PlaceRowTarget;
}

/** Which rows: the three-row root, the shared drives, or the connections. */
export type PlaceRowGroup = "root" | "drives" | "apps";

const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/** The rows a places listing shows. A connection with several siblings
 *  under one app reads "SharePoint · Investments" so the app's name stays
 *  visible and the sites read as siblings. */
export const placeRowItems = (
  places: Places,
  group: PlaceRowGroup
): PlaceRowItem[] => {
  const driveIcon = (
    <HardDrivesIcon size={18} aria-hidden className="shrink-0" />
  );
  if (group === "drives") {
    return places.sharedDrives.map((drive) => ({
      key: `drive:${drive.driveId}`,
      label: drive.name,
      // No lede on a drive (round 18b): the app mark says what the drive is
      // synced from, and its kind explained nothing.
      lede: null,
      icon: drive.connector ? <AppMark app={drive.connector} /> : driveIcon,
      target: { driveId: drive.driveId, path: [] },
    }));
  }
  if (group === "apps") {
    return places.connectedApps.flatMap((app) =>
      app.connections.map((connection) => ({
        key: `connection:${connection.key}`,
        label: connectionDisplayName(app, connection),
        lede: null,
        icon: <AppMark app={app.app} />,
        target: { driveId: connection.driveId, path: connection.path },
      }))
    );
  }
  const items: PlaceRowItem[] = [];
  if (places.myDrive) {
    items.push({
      key: `drive:${places.myDrive.driveId}`,
      label: MY_DRIVE_LABEL,
      lede: null,
      icon: driveIcon,
      target: { driveId: places.myDrive.driveId, path: [] },
    });
  }
  if (places.sharedDrives.length > 0) {
    items.push({
      key: "drives",
      label: "Shared drives",
      lede: count(places.sharedDrives.length, "drive"),
      icon: <UsersIcon size={18} aria-hidden className="shrink-0" />,
      target: { driveId: null, path: [], view: "drives" },
    });
  }
  // Connectors are first-class (round 18e, Hunter): the connections follow
  // the drives directly, with no group row between.
  items.push(...placeRowItems(places, "apps"));
  return items;
};

const PlaceRows: React.FC<{
  places: Places;
  group: PlaceRowGroup;
  onOpenPlace: (target: PlaceRowTarget) => void;
  /** The phone's full-width rows: taller, with the caret at the far right. */
  compact?: boolean;
}> = ({ places, group, onOpenPlace, compact = false }) => (
  <ul data-testid="place-rows" className="m-0 list-none p-0">
    {placeRowItems(places, group).map((item) => (
      <li key={item.key} className="border-b border-[var(--fp-border)]">
        <button
          type="button"
          aria-label={item.label}
          onClick={() => onOpenPlace(item.target)}
          className={`group flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13.5px] text-[var(--fp-ink)] hover:bg-[var(--fp-hover-row)] ${
            compact ? "min-h-[48px]" : "min-h-[40px]"
          }`}
        >
          {item.icon}
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate">{item.label}</span>
            {item.lede && (
              <span className="truncate text-[12px] text-[var(--fp-ink-3)]">
                {item.lede}
              </span>
            )}
          </span>
          {/* The caret is the whole affordance (round 18h, Hunter): the
              row opens the place, and a word saying so was noise. */}
          <span className="inline-flex shrink-0 items-center text-[var(--fp-ink-3)] group-hover:text-[var(--fp-ink)]">
            <CaretRightIcon size={12} weight="bold" aria-hidden />
          </span>
        </button>
      </li>
    ))}
  </ul>
);

export default PlaceRows;
